<template>
  <section id="contact" class="contact" aria-labelledby="contact-title">
    <div class="contact__inner">
      <div class="contact__head">
        <span class="contact__eyebrow">{{ $t('contactSection.eyebrow', 'Get in touch') }}</span>
        <h2 id="contact-title">{{ $t('contactSection.contact_us') }}</h2>
        <p class="contact__sub">{{ $t('contactSection.contact_description') }}</p>
        <span class="contact__rule" aria-hidden="true" />
      </div>

      <div class="contact__grid">
        <!-- Call -->
        <a class="ccard ccard--primary" href="tel:+255748771551">
          <span class="ccard__ic"><Icon icon="mdi:phone-in-talk" /></span>
          <span class="ccard__label">{{ $t('contactSection.our_phone') }}</span>
          <span class="ccard__value">+255 748 771 551</span>
          <span class="ccard__hint">{{ $t("siteUi.monSun") }}</span>
        </a>

        <!-- WhatsApp -->
        <a
          class="ccard ccard--whatsapp"
          href="https://wa.me/255793444333"
          target="_blank" rel="noopener"
        >
          <span class="ccard__ic"><Icon icon="mdi:whatsapp" /></span>
          <span class="ccard__label">WhatsApp</span>
          <span class="ccard__value">+255 793 444 333</span>
          <span class="ccard__hint">{{ $t("siteUi.bookingAssistant") }}</span>
        </a>

        <!-- Email -->
        <a class="ccard" href="mailto:customercare@aboodbus.co.tz">
          <span class="ccard__ic"><Icon icon="mdi:email-outline" /></span>
          <span class="ccard__label">{{ $t('contactSection.our_mail') }}</span>
          <span class="ccard__value">customercare@aboodbus.co.tz</span>
          <span class="ccard__hint">{{ $t("siteUi.replyWithin24") }}</span>
        </a>

        <!-- Headquarters / Offices CTA -->
        <div class="ccard ccard--addr">
          <span class="ccard__ic"><Icon icon="mdi:office-building-marker-outline" /></span>
          <span class="ccard__label">{{ $t('contactSection.headquarters') }}</span>
          <span class="ccard__value">{{ $t('contactSection.headquarters_address') }}</span>
          <span class="ccard__hint">{{ $t("siteUi.offices12") }}</span>
          <a href="#our-trip" class="ccard__link">
            Find an office near you <Icon icon="mdi:arrow-right" />
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { Icon } from "@iconify/vue";

export default {
  name: "ContactSection",
  components: { Icon },
};
</script>

<style scoped>
.contact {
  background: var(--color-bg);
  padding: 56px 0 72px;
}
.contact__inner {
  /* Full viewport width with comfortable side padding — matches
   * the hero's full-bleed treatment so the contact section feels
   * part of the same page rhythm. */
  width: 100%;
  margin: 0 auto;
  padding: 0 48px;
}

/* Header — same rhythm as About / Routes / Offices */
.contact__head { text-align: center; margin-bottom: 28px; }
.contact__eyebrow {
  display: inline-block;
  font: 800 12px/1 var(--font-sans);
  letter-spacing: 0.16em; text-transform: uppercase;
  /* Brand RED — ties the eyebrow into the red rule below the title
   * and the wider red-accent system across the page. */
  color: var(--color-red, #e11d48);
  margin-bottom: 10px;
}
.contact__head h2 {
  margin: 0 auto;
  font: 800 clamp(24px, 3vw, 36px) / 1.15 var(--font-sans);
  color: var(--color-text);
  letter-spacing: -0.01em;
}
.contact__sub {
  margin: 10px auto 0;
  max-width: 540px;
  font: 500 14.5px/1.55 var(--font-sans);
  color: var(--color-muted);
}
.contact__rule {
  display: block;
  width: 56px; height: 3px;
  margin: 14px auto 0;
  background: linear-gradient(90deg, var(--color-red, #e11d48) 0%, #ff6b9a 100%);
  border-radius: 2px;
}

/* Contact-method grid */
.contact__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.ccard {
  position: relative;
  display: flex; flex-direction: column; gap: 4px;
  padding: 22px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  text-decoration: none;
  color: var(--color-text);
  cursor: pointer;
  transition: transform .15s, border-color .15s, box-shadow .2s;
  overflow: hidden;
}
.ccard:hover {
  transform: translateY(-3px);
  border-color: rgba(17, 88, 171, 0.25);
  box-shadow: 0 14px 28px rgba(8, 23, 49, 0.08);
}
.ccard__ic {
  display: inline-flex; align-items: center; justify-content: center;
  width: 48px; height: 48px;
  background: var(--color-primary-50);
  color: var(--color-primary);
  border-radius: 14px;
  font-size: 24px;
  margin-bottom: 10px;
}
.ccard__label {
  font: 700 11.5px/1 var(--font-sans);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-muted);
}
.ccard__value {
  font: 800 15.5px/1.3 var(--font-sans);
  color: var(--color-text);
  word-break: break-word;
  margin-top: 2px;
}
.ccard__hint {
  margin-top: 6px;
  font: 500 12.5px/1.4 var(--font-sans);
  color: var(--color-muted);
}
.ccard__link {
  margin-top: 12px;
  display: inline-flex; align-items: center; gap: 6px;
  font: 700 12.5px/1 var(--font-sans);
  color: var(--color-primary);
  text-decoration: none;
}
.ccard__link:hover { color: var(--color-primary-600); }

/* Accent variants — distinct icon tint per card so the row feels
 * deliberate rather than four identical tiles. */
.ccard--primary .ccard__ic {
  background: linear-gradient(135deg, var(--color-primary) 0%, #1747a8 100%);
  color: #fff;
}
.ccard--whatsapp .ccard__ic {
  background: rgba(37, 211, 102, 0.12);
  color: #25D366;
}
.ccard--addr .ccard__ic {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

/* Mobile */
@media (max-width: 980px) {
  .contact__grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 980px) {
  .contact__inner { padding: 0 24px; }
}
@media (max-width: 640px) {
  .contact { padding: 40px 0 56px; }
  .contact__inner { padding: 0 16px; }
  .contact__grid { grid-template-columns: 1fr; gap: 12px; }
  .ccard { padding: 18px; }
}
</style>
